import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import { catalogAdmin, catalog } from '../api/index.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { RtfEditor } from '../components/RtfEditor.js';
import { rtfToHtml, htmlToRtf } from '../utils/rtf.js';
import { Loader2, AlertCircle, Pencil, X, Check, Layers } from 'lucide-react';
import { EnumPickList } from '../components/EnumPickList.js';
import { format } from 'date-fns';
import { useEnableEdit } from '../hooks/useAuth.js';

const SENTINEL = 'ig.field.type.delete';

function stripRtf(s: string): string {
  if (!s || !s.trim().startsWith('{\\rtf')) return s;
  return s
    .replace(/\{\\[^{}]*\}/g, '')
    .replace(/\\[a-z]+\d*[ ]?/gi, '')
    .replace(/\\\\/g, '\\')
    .replace(/\\[{}]/g, '')
    .replace(/[{}]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function sv(v: unknown): string {
  if (!v || v === SENTINEL) return '';
  return String(v);
}

function normalizeId(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as any;
    return o.$oid ?? o._id ?? String(v);
  }
  return String(v ?? '');
}

function igluArr(v: unknown): string[] {
  if (!Array.isArray(v) || v.length === 0) return [];
  return v
    .map(normalizeId)
    .filter(x => x && x !== SENTINEL);
}

function fmtDate(v: unknown): string {
  const s = sv(v);
  if (!s) return '';
  try { return format(new Date(s), 'MMM d, yyyy'); } catch { return s; }
}

function imgUrl(v: unknown): string | undefined {
  if (!v || v === SENTINEL) return undefined;
  if (typeof v === 'string') return v || undefined;
  if (typeof v === 'object') {
    const o = v as any;
    return o.url ?? o.src ?? o.href ?? undefined;
  }
  return undefined;
}

const INPUT_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';
const TEXTAREA_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[80px] resize-y';

const SERIES_STATUS_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="w-44 text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{value}</span>
    </div>
  );
}

interface SeriesForm {
  name: string;
  slug: string;
  status: string;
  description: string;
  webSynopsis: string;
  sampleUrl: string;
  imageUrl: string;
  showInNew: boolean;
  imprints: string[];
  releaseAt: string;
  originalReleaseAt: string;
  isArchived: boolean;
  publishers: string[];
  genres: string[];
}

export function SeriesDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useEnableEdit();
  const [editing, setEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<SeriesForm>({
    name: '', slug: '', status: '', description: '', webSynopsis: '',
    sampleUrl: '', imageUrl: '', showInNew: false, imprints: [], releaseAt: '',
    originalReleaseAt: '', isArchived: false, publishers: [], genres: [],
  });

  const { data: series, isLoading, error } = useQuery({
    queryKey: ['series-detail', id],
    queryFn: () => catalogAdmin.seriesDetail(id!),
    enabled: !!id,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['catalog-groups', id],
    queryFn: () => catalog.groups(id!),
    enabled: !!id,
  });

  const { data: seriesItemsData } = useQuery({
    queryKey: ['catalog-items-by-series', id],
    queryFn: () => catalog.items({ series: id!, limit: 20, sortBy: 'releaseAt', sortDir: 'asc' }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });

  const { data: publishersData } = useQuery({
    queryKey: ['catalog-publishers'],
    queryFn: catalog.publishers,
    staleTime: 10 * 60 * 1000,
  });

  const { data: imprintsData } = useQuery({
    queryKey: ['catalog-imprints'],
    queryFn: catalog.imprints,
    staleTime: 10 * 60 * 1000,
  });

  const { data: genresData } = useQuery({
    queryKey: ['catalog-genres'],
    queryFn: catalog.genres,
    staleTime: 10 * 60 * 1000,
  });

  const castForIds = (series as { cast?: unknown } | undefined)?.cast;
  const contributorTmIds = useMemo(() => {
    const raw = castForIds;
    if (!Array.isArray(raw)) return [];
    const ids = new Set<string>();
    for (const c of raw as Array<Record<string, unknown>>) {
      const tmId = normalizeId(c.teamMemberId ?? c.teamMember);
      if (tmId) ids.add(tmId);
    }
    return [...ids];
  }, [castForIds]);

  const contributorDetailQueries = useQueries({
    queries: contributorTmIds.map((tid) => ({
      queryKey: ['catalog-contributor-detail', tid],
      queryFn: () => catalogAdmin.contributorDetail(tid),
      enabled: contributorTmIds.length > 0 && !!series,
      staleTime: 10 * 60 * 1000,
    })),
  });

  const contributorMapResolved = useMemo(() => {
    const m = new Map<string, string>();
    contributorTmIds.forEach((tid, i) => {
      const d = contributorDetailQueries[i]?.data as { name?: string } | undefined;
      m.set(tid, typeof d?.name === 'string' ? d.name : tid);
    });
    return m;
  }, [contributorTmIds, contributorDetailQueries]);

  const publisherMap = new Map((publishersData?.publishers ?? []).map(p => [p._id, p.value]));
  const imprintMap = new Map((imprintsData?.imprints ?? []).map(p => [p._id, p.value]));
  const genreMap = new Map((genresData?.genres ?? []).map(g => [g._id, g.name]));
  const publishersList = publishersData?.publishers ?? [];
  const imprintsList = imprintsData?.imprints ?? [];
  const genresList = genresData?.genres ?? [];

  useEffect(() => {
    if (series) {
      const pubs = igluArr((series as any).publishers);
      const gens = igluArr((series as any).genres);
      setForm({
        name: sv(series.name),
        slug: sv(series.slug),
        status: sv(series.status),
        description: sv(series.description),
        webSynopsis: rtfToHtml(sv((series as any).webSynopsis)),
        sampleUrl: sv((series as any).sampleUrl),
        imageUrl: imgUrl(series.image) ?? '',
        showInNew: Boolean((series as any).showInNew),
        imprints: igluArr((series as any).imprints),
        releaseAt: sv(series.releaseAt)?.slice(0, 16) ?? '',
        originalReleaseAt: sv((series as any).originalReleaseAt)?.slice(0, 16) ?? '',
        isArchived: Boolean((series as any).isArchived),
        publishers: pubs,
        genres: gens,
      });
    }
  }, [series]);

  function togglePublisher(pid: string) {
    setForm(f => ({
      ...f,
      publishers: f.publishers.includes(pid)
        ? f.publishers.filter(p => p !== pid)
        : [...f.publishers, pid],
    }));
  }

  function toggleImprint(iid: string) {
    setForm(f => ({
      ...f,
      imprints: f.imprints.includes(iid)
        ? f.imprints.filter(x => x !== iid)
        : [...f.imprints, iid],
    }));
  }

  function toggleGenre(gid: string) {
    setForm(f => ({
      ...f,
      genres: f.genres.includes(gid)
        ? f.genres.filter(g => g !== gid)
        : [...f.genres, gid],
    }));
  }

  async function handleSave() {
    setSaveStatus('saving');
    setSaveError('');
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        slug: form.slug,
        status: form.status,
        description: form.description,
        webSynopsis: htmlToRtf(form.webSynopsis) || SENTINEL,
        sampleUrl: form.sampleUrl || SENTINEL,
        image: form.imageUrl || SENTINEL,
        showInNew: form.showInNew,
        imprints: form.imprints.length > 0 ? form.imprints : SENTINEL,
        isArchived: form.isArchived,
        publishers: form.publishers,
        genres: form.genres,
      };
      if (form.releaseAt) payload.releaseAt = `${form.releaseAt}:00.000`;
      if (form.originalReleaseAt) payload.originalReleaseAt = `${form.originalReleaseAt}:00.000`;
      await catalogAdmin.updateSeries(id!, payload);
      await qc.invalidateQueries({ queryKey: ['series-detail', id] });
      await qc.invalidateQueries({ queryKey: ['catalog-series'] });
      setSaveStatus('saved');
      setEditing(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !series) {
    return (
      <div className="p-8 flex items-center gap-3 text-red-600">
        <AlertCircle size={20} />
        <p>{error instanceof Error ? error.message : 'Series not found'}</p>
      </div>
    );
  }

  const cover = imgUrl(series.image);
  const groups = groupsData?.groups ?? [];
  const publisherIds = igluArr((series as any).publishers);
  const genreIds = igluArr((series as any).genres);
  const publisherNames = publisherIds.map(pid => publisherMap.get(pid) ?? pid).join(', ');
  const genreNames = genreIds.map(gid => genreMap.get(gid) ?? gid).join(', ');
  const imprintIds = igluArr((series as any).imprints);
  const imprintNames = imprintIds.map(iid => imprintMap.get(iid) ?? iid).join(', ');
  const castArr = Array.isArray((series as any).cast) ? (series as any).cast : [];

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <Link to="/series" className="text-sm text-brand-600 hover:underline mb-2 inline-block">← Back to Series</Link>
        <div className="flex items-start gap-4">
          {cover ? (
            <img src={cover} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200 flex-shrink-0"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Layers size={20} className="text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">{sv(series.name) || '(no title)'}</h1>
            <div className="flex items-center gap-3 mt-1">
              {series.status && <Badge variant={statusBadge(sv(series.status))}>{sv(series.status)}</Badge>}
              <span className="text-xs text-gray-400 font-mono">{id}</span>
            </div>
          </div>
          {!editing && canEdit && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <Card>
          <CardHeader><CardTitle>Edit Series</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input className={INPUT_CLS} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input className={INPUT_CLS} value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <SearchableSelect
                  value={form.status}
                  onChange={(v) => setForm((f) => ({ ...f, status: v }))}
                  options={SERIES_STATUS_OPTIONS}
                  placeholder="Status"
                  allowClear={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sample URL</label>
                <input className={INPUT_CLS} type="url" value={form.sampleUrl} onChange={e => setForm(f => ({ ...f, sampleUrl: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cover Image URL</label>
                <input className={INPUT_CLS} type="url" value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                  placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Release Date</label>
                <input type="datetime-local" className={INPUT_CLS} value={form.releaseAt} onChange={e => setForm(f => ({ ...f, releaseAt: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Release</label>
                <input type="datetime-local" className={INPUT_CLS} value={form.originalReleaseAt} onChange={e => setForm(f => ({ ...f, originalReleaseAt: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea className={TEXTAREA_CLS} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Web Synopsis</label>
              <RtfEditor value={form.webSynopsis} onChange={v => setForm(f => ({ ...f, webSynopsis: v }))} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publishers</label>
                {publishersList.length > 0 ? (
                  <EnumPickList
                    items={publishersList}
                    selectedIds={form.publishers}
                    onToggle={togglePublisher}
                    filterPlaceholder="Filter publishers…"
                  />
                ) : (
                  <p className="text-sm text-gray-400">Loading publishers…</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Imprints</label>
                {imprintsList.length > 0 ? (
                  <EnumPickList
                    items={imprintsList}
                    selectedIds={form.imprints}
                    onToggle={toggleImprint}
                    filterPlaceholder="Filter imprints…"
                  />
                ) : (
                  <p className="text-sm text-gray-400">Loading imprints…</p>
                )}
              </div>
            </div>
            {/* Genres */}
            {genresList.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Genres</label>
                <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {genresList.map(g => (
                    <label key={g._id} className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input type="checkbox" checked={form.genres.includes(g._id)} onChange={() => toggleGenre(g._id)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      <span className="text-sm text-gray-700">{g.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.showInNew} onChange={e => setForm(f => ({ ...f, showInNew: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="font-medium text-gray-700">Show in New</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.isArchived} onChange={e => setForm(f => ({ ...f, isArchived: e.target.checked }))}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="font-medium text-gray-700">Archived</span>
              </label>
            </div>
            {saveStatus === 'error' && <p className="text-sm text-red-600">{saveError}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => { setEditing(false); setSaveStatus('idle'); }}>
                <X size={14} /> Cancel
              </Button>
              <Button onClick={handleSave} loading={saveStatus === 'saving'}>
                <Check size={14} /> Save
              </Button>
            </div>
            {saveStatus === 'saved' && <p className="text-sm text-green-600 text-right">Saved ✓</p>}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Core details */}
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-0">
              <Row label="Name" value={sv(series.name)} />
              <Row label="Slug" value={sv(series.slug)} />
              <Row label="Status" value={sv(series.status)} />
              <Row label="Release Date" value={fmtDate(series.releaseAt)} />
              <Row label="Original Release" value={fmtDate((series as any).originalReleaseAt)} />
              <Row label="Publishers" value={publisherNames || undefined} />
              <Row label="Genres" value={genreNames || undefined} />
              <Row label="Imprints" value={imprintNames || undefined} />
              <Row label="Sample URL" value={sv((series as any).sampleUrl)} />
              <Row label="Show In New" value={(series as any).showInNew ? 'Yes' : ''} />
              <Row label="Archived" value={(series as any).isArchived ? 'Yes' : ''} />
              <Row label="Created" value={fmtDate((series as any).createdAt)} />
              <Row label="Updated" value={fmtDate((series as any).updatedAt)} />
            </CardContent>
          </Card>

          {/* Description */}
          {(sv(series.description) || sv((series as any).webSynopsis)) && (
            <Card>
              <CardHeader><CardTitle>Description</CardTitle></CardHeader>
              <CardContent className="space-y-0">
                <Row label="Description" value={sv(series.description)} />
                <Row label="Web Synopsis" value={stripRtf(sv((series as any).webSynopsis)) || undefined} />
              </CardContent>
            </Card>
          )}

          {/* Cast */}
          {castArr.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Cast ({castArr.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {castArr.map((c: any, i: number) => {
                    const tmId = normalizeId(c.teamMemberId ?? c.teamMember);
                    const name = contributorMapResolved.get(tmId) ?? tmId;
                    const roles = Array.isArray(c.roles) ? c.roles.join(', ') : sv(c.roles ?? c.type);
                    return (
                      <div key={i} className="flex items-center gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
                        <Link to={`/contributors/${tmId}`} className="text-brand-600 hover:underline w-48 flex-shrink-0">{name}</Link>
                        <span className="text-gray-500 text-xs">{roles}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Groups in this series */}
      {groups.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Groupings ({groups.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {groups.map(g => (
                <div
                  key={g._id}
                  onClick={() => navigate(`/groups/${g._id}`)}
                  className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 rounded px-2"
                >
                  <span className="text-sm text-gray-900">{g.name}</span>
                  {g.status && <Badge variant={statusBadge(g.status)}>{g.status}</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items in this series */}
      {(() => {
        const items = seriesItemsData?.items ?? [];
        const total = seriesItemsData?.total ?? 0;
        if (total === 0) return null;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Items ({total})</CardTitle>
                <Link
                  to={`/catalog?series=${id}`}
                  className="text-xs text-brand-600 hover:underline"
                >
                  View all →
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Title</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Status</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Release</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map(item => {
                      const rel = item.releaseAt ? (() => { try { return format(new Date(item.releaseAt as string), 'MMM d, yyyy'); } catch { return item.releaseAt as string; } })() : '';
                      return (
                        <tr
                          key={item._id}
                          onClick={() => navigate(`/catalog/${item._id}`)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-2 pr-4 font-medium text-gray-900 truncate max-w-[14rem]">{item.name}</td>
                          <td className="py-2 pr-4">
                            {item.status ? <Badge variant={statusBadge(item.status)}>{item.status}</Badge> : '—'}
                          </td>
                          <td className="py-2 text-gray-500 text-xs whitespace-nowrap">{rel || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {total > 20 && (
                <p className="text-xs text-gray-400 mt-3">Showing 20 of {total}. <Link to={`/catalog?series=${id}`} className="text-brand-600 hover:underline">View all →</Link></p>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
